import { useState } from "react";
import { avatarPictureUrl } from "../../api/avatarsApi";
import { classNames } from "../styles/classNames";
import { useAvatarVersion } from "./avatarVersions";
import GeneratedAvatar from "./generated/GeneratedAvatar";

export type PersonAvatarSize = "xs" | "sm" | "md" | "lg";

const SIZE_CLASSES: Readonly<Record<PersonAvatarSize, string>> = {
  xs: "app-avatar-xs",
  sm: "app-avatar-sm",
  md: "app-avatar-md",
  lg: "app-avatar-lg",
};

interface PersonAvatarProps {
  /** `null` draws the avatar from the name, for a person the page cannot name by ID. */
  userId: string | null;
  name: string;
  size?: PersonAvatarSize;
  className?: string;
}

/**
 * A person's avatar (F14): their uploaded picture, over the avatar generated for them,
 * which shows while it loads, when it fails and when there is none. The generated one is
 * drawn from the account ID: unique, fixed when the account is made, and unchanged when
 * the person renames themselves. Decorative: the name always stands beside it.
 */
export default function PersonAvatar({ userId, name, size = "md", className }: PersonAvatarProps) {
  const version = useAvatarVersion(userId);
  const source = userId === null || version === null ? null : avatarPictureUrl(userId, version);
  const [loadedSource, setLoadedSource] = useState<string | null>(null);

  return (
    <span aria-hidden="true" className={classNames("app-avatar", SIZE_CLASSES[size], className)}>
      <GeneratedAvatar seed={userId ?? name} />
      {source === null ? null : (
        <img
          src={source}
          alt=""
          decoding="async"
          className={classNames("app-avatar-picture", loadedSource === source && "app-avatar-picture-loaded")}
          onLoad={() => {
            setLoadedSource(source);
          }}
        />
      )}
    </span>
  );
}
